import { useTranslation } from 'react-i18next';

import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';

import styles from './styles';

type Props<T> = {
  options: T[];
  loading: boolean;
  searchTerm: string;
  placeholder?: string;
  defaultValue?: boolean;
  onOptionSelect: (_: React.SyntheticEvent, value: T | null) => void;
  onInputChange: (_: React.SyntheticEvent, value: string, reason: string) => void;
  onReset: () => void;
  getCustomOptionLabel?: (option: T) => string;
};

const SearchComponent = <T,>({
  options,
  loading,
  searchTerm,
  placeholder,
  defaultValue = false,
  onInputChange,
  onOptionSelect,
  getCustomOptionLabel,
  onReset,
}: Props<T>) => {
  const { t } = useTranslation();

  const getOptionLabel = (option: T) => {
    if (getCustomOptionLabel) {
      return getCustomOptionLabel(option);
    }
    if ((option as any).node) {
      const { node } = option as any;
      return node.shortName !== '<unnamed reference customer>'
        ? `${node.shortName} - ${node.uniqueDomain}`
        : node.uniqueDomain;
    }
    return '';
  };

  return (
    <Autocomplete
      freeSolo
      sx={styles.autocomplete}
      loading={loading}
      getOptionLabel={getOptionLabel}
      options={options}
      defaultValue={defaultValue ? options[0] : null}
      size="small"
      inputValue={searchTerm}
      onInputChange={onInputChange}
      onChange={onOptionSelect}
      renderInput={params => (
        <TextField
          {...params}
          label={placeholder !== undefined && placeholder !== '' ? placeholder : undefined}
          variant="outlined"
          fullWidth
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} sx={{ color: '#3F5D66' }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      PaperComponent={props => (
        <Box
          {...props}
          sx={{
            backgroundColor: 'var(--surface-base)',
            borderRadius: 'var(--radius-none)',
            boxShadow: 'none',
            border: 'var(--elevation-2)',
            mt: 0,
            '& .MuiAutocomplete-listbox': {
              backgroundColor: 'var(--surface-base)',
              '& .MuiAutocomplete-option': {
                fontSize: 'var(--text-sm)',
                fontFamily: 'var(--font-data)',
                py: 1,
                px: 2,
                color: 'var(--text-primary)',
                backgroundColor: 'var(--surface-base)',
                '&:hover': {
                  backgroundColor: 'var(--surface-raised)',
                  color: 'var(--text-primary)',
                },
                '&[aria-selected="true"]': {
                  backgroundColor: 'var(--surface-overlay)',
                  color: 'var(--text-primary)',
                  '&:hover': {
                    backgroundColor: 'var(--surface-raised)',
                    color: 'var(--text-primary)',
                  },
                },
              },
            },
          }}
        />
      )}
    />
  );
};

export default SearchComponent;
